
import React, { useEffect, useState } from "react";
import API_URL from "../../../api/API";
import { useAdminAuthContext } from "../../admin/useAdminAuthContext";

// ========================================
// PRODUCT APPROVAL
// ========================================

const ProductApproval = ({
    product,
    onUpdated
}) => {

    const { admin } = useAdminAuthContext();

    const [status, setStatus] = useState("draft");
    const [notes, setNotes] = useState("");
    const [isActive, setIsActive] = useState(true);

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // ========================================
    // INITIALIZE
    // ========================================

    useEffect(() => {

        if (!product) return;

        setStatus(product.approval?.status || "draft");
        setNotes(product.approval?.notes || "");
        setIsActive(product.isActive !== false);

    }, [product]);

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    // ========================================
    // SAVE APPROVAL
    // ========================================

    const handleSave = async () => {

        setError("");
        setSuccess("");

        if (!product?._id) {
            setError("Save the product before updating its approval.");
            return;
        }

        if (!admin?.token) {
            setError("Administrator authentication is required.");
            return;
        }

        setIsSaving(true);

        try {

            const response = await fetch(
                API_URL.ADMIN_PRODUCT_APPROVAL + product._id,
                {
                    method: "PUT",

                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${admin.token}`
                    },

                    body: JSON.stringify({
                        status,
                        notes,
                        isActive
                    })
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(
                    result.error ||
                    result.message ||
                    "Unable to update product approval."
                );
            }

            // Refresh the editor using the authoritative
            // product returned by the backend, if available.

            if (typeof onUpdated === "function") {
                await onUpdated();
            }

            setSuccess("Product approval updated successfully.");

        } catch (err) {

            setError(err.message);

        } finally {

            setIsSaving(false);

        }

    };

    // ========================================
    // STATUS INFORMATION
    // ========================================

    const getStatusDescription = () => {

        switch (status) {

            case "draft":
                return "Product knowledge is being prepared or edited. It should not be available to the customer-facing AI agent.";

            case "review":
                return "Product knowledge is awaiting review and verification before approval.";

            case "approved":
                return "Product knowledge has been approved. It may be made available to the AI agent when the product is also active.";

            default:
                return "";

        }

    };

    const approvedBy = product?.approval?.approvedBy;

    const approvedByLabel = approvedBy
        ? typeof approvedBy === "object"
            ? (
                approvedBy.name ||
                approvedBy.email ||
                approvedBy._id ||
                "Administrator"
            )
            : approvedBy
        : "Not approved";

    const approvedAt = product?.approval?.approvedAt;

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-8">

            {/* HEADER */}

            <div className="border-b pb-4">

                <h3 className="text-base font-semibold text-gray-900">
                    Product Approval & Status
                </h3>

                <p className="text-sm text-gray-500 mt-1">
                    Control the review, approval and activation
                    of this product's knowledge.
                </p>

            </div>

            {/* UNSAVED PRODUCT */}

            {!product?._id && (

                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">

                    <p className="text-sm text-amber-800">
                        This product must be saved before its
                        approval status can be changed.
                    </p>

                </div>

            )}

            {/* APPROVAL STATUS */}

            <section className="space-y-4">

                <div>

                    <label className={labelClass}>
                        Approval Status
                    </label>

                    <select
                        value={status}
                        onChange={(event) => {
                            setStatus(event.target.value);
                            setSuccess("");
                        }}
                        className={inputClass}
                    >
                        <option value="draft">
                            Draft
                        </option>

                        <option value="review">
                            In Review
                        </option>

                        <option value="approved">
                            Approved
                        </option>
                    </select>

                </div>

                <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">

                    <p className="text-sm text-gray-700">
                        {getStatusDescription()}
                    </p>

                </div>

            </section>

            {/* PRODUCT ACTIVATION */}

            <section className="space-y-4 border-t pt-6">

                <h3 className="text-base font-semibold text-gray-900">
                    Product Activation
                </h3>

                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">

                    <label className="flex items-start gap-3 cursor-pointer">

                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(event) => {
                                setIsActive(event.target.checked);
                                setSuccess("");
                            }}
                            className="mt-1 h-4 w-4"
                        />

                        <div>

                            <span className="block text-sm font-medium text-gray-900">
                                Product Active
                            </span>

                            <p className="text-xs text-gray-500 mt-1">
                                Only approved and active products
                                should be available through the
                                customer-facing AI product tools.
                            </p>

                        </div>

                    </label>

                </div>

            </section>

            {/* APPROVAL NOTES */}

            <section className="space-y-4 border-t pt-6">

                <h3 className="text-base font-semibold text-gray-900">
                    Approval Notes
                </h3>

                <textarea
                    value={notes}
                    onChange={(event) => {
                        setNotes(event.target.value);
                        setSuccess("");
                    }}
                    placeholder="Enter review comments, corrections required, or approval notes."
                    rows={5}
                    className={inputClass}
                />

            </section>

            {/* APPROVAL DETAILS */}

            <section className="space-y-4 border-t pt-6">

                <h3 className="text-base font-semibold text-gray-900">
                    Approval Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <div className="border border-gray-200 rounded-lg p-4">

                        <p className="text-xs text-gray-500">
                            Approved By
                        </p>

                        <p className="text-sm font-medium text-gray-900 mt-2">
                            {approvedByLabel}
                        </p>

                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">

                        <p className="text-xs text-gray-500">
                            Approved At
                        </p>

                        <p className="text-sm font-medium text-gray-900 mt-2">

                            {approvedAt
                                ? new Date(approvedAt).toLocaleString()
                                : "Not approved"
                            }

                        </p>

                    </div>

                </div>

            </section>

            {/* MESSAGES */}

            {error && (

                <div className="border border-red-200 bg-red-50 rounded-lg p-4">

                    <p className="text-sm text-red-700">
                        {error}
                    </p>

                </div>

            )}

            {success && (

                <div className="border border-green-200 bg-green-50 rounded-lg p-4">

                    <p className="text-sm text-green-700">
                        {success}
                    </p>

                </div>

            )}

            {/* SAVE BUTTON */}

            <div className="border-t pt-6 flex justify-end">

                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving || !product?._id}
                    className="px-6 py-3 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-800 disabled:opacity-50"
                >
                    {isSaving
                        ? "Updating..."
                        : "Update Approval & Status"
                    }
                </button>

            </div>

        </div>

    );

};

export default ProductApproval;
